import { Component, Input, OnInit } from '@angular/core';
import { ILearner } from 'app/interfaces/learner.interface';
import { ITeacher } from 'app/interfaces/teacher.interface';

@Component({
  selector: 'app-id-card',
  templateUrl: './id-card.component.html',
  styleUrls: ['./id-card.component.scss']
})
export class IdCardComponent implements OnInit {
  @Input() user: ILearner | ITeacher;
  @Input() school: any;
  @Input() academicYear: any;
  @Input() userType: 'learner' | 'teacher' = 'learner';

  currentYear = new Date().getFullYear();
  isDownloading = false;

  ngOnInit() {
    // Initialize component
  }

  getUserName(): string {
    if (this.user) {
      return `${this.user.first_name || ''} ${this.user.last_name || ''}`.trim();
    }
    return '';
  }

  getUserId(): string | number {
    if (this.user) {
      if (this.userType === 'learner') {
        return (this.user as ILearner).matricule || 'N/A';
      } else {
        return (this.user as ITeacher).id || 'N/A';
      }
    }
    return 'N/A';
  }

  getUserPhoto(): string {
    if (this.user && this.user.profile_photo) {
      return this.user.profile_photo;
    }
    return 'assets/img/male-student.png';
  }

  getUserClass(): string {
    if (this.userType === 'learner' && this.user) {
      const learner = this.user as ILearner;
      return learner.classroom?.name || 'N/A';
    }
    return 'N/A';
  }

  getUserDepartment(): string {
    if (this.userType === 'teacher' && this.user) {
      const teacher = this.user as ITeacher;
      return teacher.department?.name || 'N/A';
    }
    return 'N/A';
  }

  downloadCard() {
    this.isDownloading = true;
    
    // Use html2canvas to capture the card
    import('html2canvas').then(html2canvas => {
      const element = document.getElementById('id-card');
      if (element) {
        html2canvas.default(element, {
          scale: 3, // Increased scale for better quality
          useCORS: true,
          allowTaint: true,
          backgroundColor: '#ffffff',
          logging: false,
          width: 340, // Standard ID card width
          height: 216  // Standard ID card height
        }).then(canvas => {
          // Convert to blob and download
          canvas.toBlob((blob) => {
            if (blob) {
              const url = window.URL.createObjectURL(blob);
              const link = document.createElement('a');
              link.href = url;
              link.download = `id_card_${this.getUserId()}_${this.currentYear}.png`;
              document.body.appendChild(link);
              link.click();
              document.body.removeChild(link);
              window.URL.revokeObjectURL(url);
            }
            this.isDownloading = false;
          }, 'image/png', 0.95); // High quality PNG
        }).catch(error => {
          console.error('Error generating ID card:', error);
          this.isDownloading = false;
        });
      }
    }).catch(error => {
      console.error('Error loading html2canvas:', error);
      this.isDownloading = false;
    });
  }

  printCard() {
    const printWindow = window.open('', '_blank');
    if (printWindow) {
      const element = document.getElementById('id-card');
      if (element) {
        printWindow.document.write(`
          <html>
            <head>
              <title>ID Card - ${this.getUserName()}</title>
              <style>
                @media print {
                  body { 
                    margin: 0; 
                    padding: 10px; 
                    font-family: 'Inter', Arial, sans-serif; 
                    background: white;
                  }
                  .id-card { 
                    width: 85.6mm; 
                    height: 54mm; 
                    border: 2px solid #000; 
                    border-radius: 8px; 
                    padding: 10px; 
                    box-sizing: border-box;
                    background: white;
                    color: #1f2937;
                    font-size: 12px;
                    box-shadow: none;
                    break-inside: avoid;
                    page-break-inside: avoid;
                  }
                  .card-header { text-align: center; margin-bottom: 10px; }
                  .card-body { display: flex; gap: 15px; }
                  .photo-section { flex-shrink: 0; }
                  .photo { width: 40px; height: 40px; border-radius: 50%; border: 2px solid #374151; }
                  .info-section { flex-grow: 1; }
                  .info-row { margin-bottom: 5px; display: flex; justify-content: space-between; }
                  .label { font-weight: bold; font-size: 10px; color: #6b7280; }
                  .value { font-size: 11px; color: #1f2937; }
                  .school-logo { width: 30px; height: 30px; margin: 0 auto 5px; }
                  .card-footer { margin-top: 10px; font-size: 10px; color: #6b7280; }
                }
              </style>
            </head>
            <body>
              ${element.outerHTML}
            </body>
          </html>
        `);
        printWindow.document.close();
        printWindow.focus();
        setTimeout(() => {
          printWindow.print();
        }, 500);
      }
    }
  }

  shareCard() {
    // Check if Web Share API is available
    if (navigator.share) {
      navigator.share({
        title: `${this.getUserName()} - ID Card`,
        text: `ID Card for ${this.getUserName()} from ${this.school?.name || 'School'}`,
        url: window.location.href
      }).catch(error => {
        this.fallbackShare();
      });
    } else {
      this.fallbackShare();
    }
  }

  private fallbackShare() {
    // Fallback for browsers that don't support Web Share API
    const shareText = `ID Card for ${this.getUserName()} from ${this.school?.name || 'School'}`;
    const shareUrl = window.location.href;
    
    // Try to copy to clipboard
    if (navigator.clipboard) {
      navigator.clipboard.writeText(`${shareText}\n${shareUrl}`).then(() => {
        // Show success message (you can integrate with your notification service)
      }).catch(() => {
        // Fallback to alert
        alert(`${shareText}\n${shareUrl}\n\nLink copied to clipboard`);
      });
    } else {
      // Final fallback
      prompt('Share this link:', `${shareText}\n${shareUrl}`);
    }
  }
} 